import { SeoStructuredData } from 'src/SeoGlobal'

/* Next */
import type { AppProps, NextWebVitalsMetric } from 'next/app'

/* React */
import { useEffect } from 'react'

/* Redux */
import { Provider } from 'react-redux'
import { storeWrapper } from 'src/redux/store'
import { useSelector } from 'src/redux/hooks'

/* Funktionen */
import CheckLocalStorage from 'src/redux/CheckLocalStorage'
import GlobalDidMountFunctions from 'src/redux/GlobalDidMountFunctions'

/* Optionen */
import config from 'src/config'

/* Material */
import { ThemeProvider, Theme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import theme from 'src/theme/theme'
import { CacheProvider, EmotionCache } from '@emotion/react'
import createEmotionCache from 'src/theme/createEmotionCache'

/* Custom */
import Layout from 'src/layout/Layout'
import 'src/theme/global.css'
import { ProductInquiryProvider } from 'src/context/ProductInquiryContext'
import { LandingPageProductInquirySettings } from 'src/shared/dbTypes/landingPageSettings'

import { GoogleAnalytics, GoogleAnalyticsPageView } from 'src/components/GoogleAnalytics'
import { Matomo, MatomoTagManager, MatomoPageView  } from 'src/components/Matomo'
import { FirstPartyTracking } from 'src/components/FirstPartyTracking'
import { analyticsConfig } from 'src/functions/analyticsConfig'
import { reportShopWebVital } from 'src/functions/webVitals'
import { isPrivateSeoPath } from 'src/functions/seo'

import DefaultSeoHead, { defaultSocialImage } from 'src/components/DefaultSeoHead'

/* PayPal */
import PayPalPageProvider from 'src/components/PayPalPageProvider'

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
  reportShopWebVital(metric)
}

const Analytics = () => {
  const cookieConsent = useSelector(store => store.App.cookieConsent)

  if (analyticsConfig.tracker === 'google_analytics') {
    const measurementId = analyticsConfig.googleAnalytics.measurementId
    if (!measurementId) return null

    return <>
      <GoogleAnalytics measurementId={measurementId} />
      <GoogleAnalyticsPageView
        measurementId={measurementId}
        enabled={cookieConsent === 'all'}
        trackInitialImmediately
      />
    </>
  }

  if (analyticsConfig.tracker !== 'matomo') return null

  const { siteId, containerId, baseUrl } = analyticsConfig.matomo
  if (!siteId || !containerId) return null
  const analyticsAllowed = cookieConsent === 'all'

  const matomoContainerId = containerId.startsWith('container_')
    ? containerId
    : `container_${containerId}`

  return <>
    <Matomo siteId={siteId} baseUrl={baseUrl} />
    {analyticsAllowed && <MatomoTagManager containerId={matomoContainerId} baseUrl={baseUrl} />}
    <MatomoPageView enabled={analyticsAllowed} trackInitialImmediately />
  </>
}

const clientEmotionCache = createEmotionCache()

const MyApp = ({Component, emotionCache = clientEmotionCache, ...rest}: AppProps & { emotionCache?: EmotionCache }): JSX.Element => {

  const { store, props } = storeWrapper.useWrappedStore(rest)

  useEffect(() => {
    // Material - Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side')
    if (jssStyles?.parentElement) jssStyles.parentElement.removeChild(jssStyles)
  }, [])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return
    if (!('serviceWorker' in navigator) || !window.isSecureContext) return

    const registerServiceWorker = () => {
      navigator.serviceWorker.register('/service-worker.js').catch(() => undefined)
    }

    if (document.readyState === 'complete') {
      registerServiceWorker()
      return
    }

    window.addEventListener('load', registerServiceWorker, { once: true })
    return () => window.removeEventListener('load', registerServiceWorker)
  }, [])

  const productInquiry = props.pageProps?.productInquiry as LandingPageProductInquirySettings | undefined

  const appContent = (
    <ProductInquiryProvider settings={productInquiry}>
      <Layout pageProps={props.pageProps}>
        <FirstPartyTracking />
        <Analytics />
        <Component {...props.pageProps} />
      </Layout>
    </ProductInquiryProvider>
  )

  const skipPayPalProvider = rest.router?.pathname === '/paypal-advanced-test'

  return <CacheProvider value={emotionCache}>

    <DefaultSeoHead privatePage={isPrivateSeoPath(rest.router?.pathname)} />
    
    <SeoStructuredData logoUrl={defaultSocialImage} />

    <ThemeProvider theme={theme}>
      
      <CssBaseline />{' ' /* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. <Matomo siteId={shopInfos.siteId} />*/}
      
      <Provider store={store}>

        {config.PayPalDisplay && !skipPayPalProvider
          ? (
            <PayPalPageProvider options={config.paypalConfig} pathname={rest.router?.pathname || ''}>
              {appContent}
            </PayPalPageProvider>
          )
          : appContent}

        <CheckLocalStorage />
        <GlobalDidMountFunctions />

      </Provider>

    </ThemeProvider>

  </CacheProvider>
  
}

export default MyApp
